import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  BookOpen,
  Plus,
  Calendar,
  TrendingUp,
  TrendingDown,
  DollarSign,
  Edit2,
  Trash2,
  Save,
  X,
  ChevronDown,
  ChevronUp,
  Target,
  AlertCircle,
  Clock,
  Filter,
  Download,
  Camera
} from 'lucide-react';
import axios from 'axios';
import { API, getAuthHeaders } from '../utils/api';
import { toast } from 'sonner';

const EMOTIONS = [
  { value: 'confident', label: 'Confident', emoji: '😎' },
  { value: 'neutral', label: 'Neutral', emoji: '😐' },
  { value: 'anxious', label: 'Anxious', emoji: '😰' },
  { value: 'fomo', label: 'FOMO', emoji: '😱' },
  { value: 'greedy', label: 'Greedy', emoji: '🤑' },
  { value: 'fearful', label: 'Fearful', emoji: '😨' },
  { value: 'patient', label: 'Patient', emoji: '🧘' },
  { value: 'frustrated', label: 'Frustrated', emoji: '😤' }
];

const TRADE_TYPES = ['Manual', 'Signal-Based', 'Auto-Trade'];

export default function TradingJournal() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddEntry, setShowAddEntry] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [expandedId, setExpandedId] = useState(null);
  const [filter, setFilter] = useState('all'); // 'all', 'win', 'loss', 'pending'
  
  const [newEntry, setNewEntry] = useState({
    market: '',
    direction: 'buy',
    entry_price: '',
    exit_price: '',
    stop_loss: '',
    take_profit: '',
    lot_size: '',
    pips: '',
    pnl: '',
    emotion_before: 'neutral',
    emotion_after: 'neutral',
    trade_type: 'Signal-Based',
    notes: '',
    lessons: '',
    screenshot_url: '',
    date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchEntries();
  }, [filter]);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/journal/entries?filter=${filter}`, {
        withCredentials: true,
        headers: getAuthHeaders(),
        timeout: 10000
      });
      
      if (response.data?.entries) {
        setEntries(response.data.entries);
      } else {
        // No entries yet - show empty state
        setEntries([]);
      }
    } catch (error) {
      console.error('Failed to fetch journal entries:', error);
      // Show empty state instead of mock data - user hasn't added entries yet
      setEntries([]);
    } finally {
      setLoading(false);
    }
  };

  const handleAddEntry = async () => {
    try {
      const response = await axios.post(`${API}/journal/entries`, newEntry, {
        withCredentials: true,
        headers: getAuthHeaders()
      });
      
      if (response.data) {
        toast.success('Journal entry added!');
        setShowAddEntry(false);
        setNewEntry({
          market: '',
          direction: 'buy',
          entry_price: '',
          exit_price: '',
          stop_loss: '',
          take_profit: '',
          lot_size: '',
          pips: '',
          pnl: '',
          emotion_before: 'neutral',
          emotion_after: 'neutral',
          trade_type: 'Signal-Based',
          notes: '',
          lessons: '',
          screenshot_url: '',
          date: new Date().toISOString().split('T')[0]
        });
        fetchEntries();
      }
    } catch (error) {
      console.error('Failed to add entry:', error);
      toast.error('Failed to add journal entry');
    }
  };

  const handleDeleteEntry = async (id) => {
    if (!confirm('Delete this journal entry?')) return;
    
    try {
      await axios.delete(`${API}/journal/entries/${id}`, {
        withCredentials: true,
        headers: getAuthHeaders()
      });
      toast.success('Entry deleted');
      fetchEntries();
    } catch (error) {
      toast.error('Failed to delete entry');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getEmotion = (value) => EMOTIONS.find(e => e.value === value) || EMOTIONS[1];

  const filteredEntries = entries.filter(entry => {
    if (filter === 'all') return true;
    return entry.result === filter;
  });

  // Calculate stats
  const stats = {
    totalTrades: entries.length,
    wins: entries.filter(e => e.result === 'win').length,
    losses: entries.filter(e => e.result === 'loss').length,
    pending: entries.filter(e => e.result === 'pending').length,
    totalPnl: entries.reduce((sum, e) => sum + (e.pnl || 0), 0),
    avgWin: entries.filter(e => e.result === 'win').reduce((sum, e) => sum + (e.pnl || 0), 0) / (entries.filter(e => e.result === 'win').length || 1),
    avgLoss: Math.abs(entries.filter(e => e.result === 'loss').reduce((sum, e) => sum + (e.pnl || 0), 0)) / (entries.filter(e => e.result === 'loss').length || 1)
  };

  return (
    <Card className="bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-800 border-zinc-700 overflow-hidden">
      {/* Header */}
      <div className="p-4 border-b border-zinc-700 bg-gradient-to-r from-zinc-900 to-zinc-800">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-gradient-to-br from-amber-500/30 to-amber-600/20 rounded-xl border border-amber-500/30">
              <BookOpen className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white flex items-center gap-2">
                Trading Journal
                <Badge variant="outline" className="bg-amber-600/20 border-amber-500/50 text-amber-400 text-xs">
                  PREMIUM
                </Badge>
              </h3>
              <p className="text-xs text-slate-400">Track trades, emotions & lessons learned</p>
            </div>
          </div>
          
          <Button
            onClick={() => setShowAddEntry(true)}
            className="bg-amber-600 hover:bg-amber-700 text-white"
            data-testid="add-journal-entry-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Add Entry
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4 border-b border-zinc-800">
        <div className="bg-zinc-800/50 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-white">{stats.totalTrades}</p>
          <p className="text-xs text-slate-500">Total Trades</p>
        </div>
        <div className="bg-emerald-500/10 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-emerald-400">{stats.wins}</p>
          <p className="text-xs text-slate-500">Wins</p>
        </div>
        <div className="bg-red-500/10 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-red-400">{stats.losses}</p>
          <p className="text-xs text-slate-500">Losses</p>
        </div>
        <div className="bg-amber-500/10 rounded-lg p-3 text-center">
          <p className="text-2xl font-bold text-amber-400">{stats.pending}</p>
          <p className="text-xs text-slate-500">Open</p>
        </div>
        <div className={`${stats.totalPnl >= 0 ? 'bg-emerald-500/10' : 'bg-red-500/10'} rounded-lg p-3 text-center`}>
          <p className={`text-2xl font-bold ${stats.totalPnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
            ${stats.totalPnl >= 0 ? '+' : ''}{stats.totalPnl.toFixed(2)}
          </p>
          <p className="text-xs text-slate-500">Total P&L</p>
        </div>
      </div>

      {/* Filter */}
      <div className="p-4 border-b border-zinc-800 flex items-center gap-2">
        <Filter className="w-4 h-4 text-slate-500" />
        {['all', 'win', 'loss', 'pending'].map((f) => (
          <Button
            key={f}
            variant="ghost"
            size="sm"
            onClick={() => setFilter(f)}
            className={`text-xs px-3 h-7 ${
              filter === f
                ? f === 'win' ? 'bg-emerald-500/20 text-emerald-400' :
                  f === 'loss' ? 'bg-red-500/20 text-red-400' :
                  f === 'pending' ? 'bg-amber-500/20 text-amber-400' :
                  'bg-white/10 text-white'
                : 'text-slate-500 hover:text-white'
            }`}
            data-testid={`filter-${f}`}
          >
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>

      {/* Add Entry Form */}
      {showAddEntry && (
        <div className="p-4 border-b border-zinc-800 bg-zinc-800/30">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-white">New Journal Entry</h4>
            <Button variant="ghost" size="sm" onClick={() => setShowAddEntry(false)}>
              <X className="w-4 h-4" />
            </Button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Market</label>
              <Input
                value={newEntry.market}
                onChange={(e) => setNewEntry({ ...newEntry, market: e.target.value.toUpperCase() })}
                placeholder="XAUUSD"
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Direction</label>
              <select
                value={newEntry.direction}
                onChange={(e) => setNewEntry({ ...newEntry, direction: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white"
              >
                <option value="buy">BUY</option>
                <option value="sell">SELL</option>
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Entry Price</label>
              <Input
                type="number"
                step="any"
                value={newEntry.entry_price}
                onChange={(e) => setNewEntry({ ...newEntry, entry_price: e.target.value })}
                placeholder="0.00"
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Exit Price</label>
              <Input
                type="number"
                step="any"
                value={newEntry.exit_price}
                onChange={(e) => setNewEntry({ ...newEntry, exit_price: e.target.value })}
                placeholder="0.00 (leave empty if open)"
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Stop Loss</label>
              <Input
                type="number"
                step="any"
                value={newEntry.stop_loss}
                onChange={(e) => setNewEntry({ ...newEntry, stop_loss: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Take Profit</label>
              <Input
                type="number"
                step="any"
                value={newEntry.take_profit}
                onChange={(e) => setNewEntry({ ...newEntry, take_profit: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Lot Size</label>
              <Input
                type="number"
                step="0.01"
                value={newEntry.lot_size}
                onChange={(e) => setNewEntry({ ...newEntry, lot_size: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">P&L ($)</label>
              <Input
                type="number"
                step="any"
                value={newEntry.pnl}
                onChange={(e) => setNewEntry({ ...newEntry, pnl: e.target.value })}
                className="bg-zinc-800 border-zinc-700"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-4">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Emotion Before</label>
              <select
                value={newEntry.emotion_before}
                onChange={(e) => setNewEntry({ ...newEntry, emotion_before: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white"
              >
                {EMOTIONS.map(em => (
                  <option key={em.value} value={em.value}>{em.emoji} {em.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Emotion After</label>
              <select
                value={newEntry.emotion_after}
                onChange={(e) => setNewEntry({ ...newEntry, emotion_after: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white"
              >
                {EMOTIONS.map(em => (
                  <option key={em.value} value={em.value}>{em.emoji} {em.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Trade Type</label>
              <select
                value={newEntry.trade_type}
                onChange={(e) => setNewEntry({ ...newEntry, trade_type: e.target.value })}
                className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2 text-sm text-white"
              >
                {TRADE_TYPES.map(type => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Trade Notes</label>
              <Textarea
                value={newEntry.notes}
                onChange={(e) => setNewEntry({ ...newEntry, notes: e.target.value })}
                placeholder="What was your reasoning for this trade?"
                className="bg-zinc-800 border-zinc-700 min-h-[80px]"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Lessons Learned</label>
              <Textarea
                value={newEntry.lessons}
                onChange={(e) => setNewEntry({ ...newEntry, lessons: e.target.value })}
                placeholder="What did you learn from this trade?"
                className="bg-zinc-800 border-zinc-700 min-h-[80px]"
              />
            </div>
          </div>
          
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setShowAddEntry(false)}>Cancel</Button>
            <Button onClick={handleAddEntry} className="bg-amber-600 hover:bg-amber-700">
              <Save className="w-4 h-4 mr-2" />
              Save Entry
            </Button>
          </div>
        </div>
      )}

      {/* Entries List */}
      <div className="p-4 max-h-[500px] overflow-y-auto custom-scrollbar">
        {loading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="animate-pulse bg-zinc-800/50 rounded-xl h-24" />
            ))}
          </div>
        ) : filteredEntries.length === 0 ? (
          <div className="text-center py-8 text-slate-500">
            <BookOpen className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No journal entries yet</p>
            <p className="text-xs mt-1">Start tracking your trades to improve your performance</p>
          </div>
        ) : (
          <div className="space-y-3">
            {filteredEntries.map((entry) => {
              const emotionBefore = getEmotion(entry.emotion_before);
              const emotionAfter = getEmotion(entry.emotion_after);
              const isExpanded = expandedId === entry.id;
              
              return (
                <div
                  key={entry.id}
                  className={`rounded-xl border ${
                    entry.result === 'win' ? 'border-emerald-500/30 bg-emerald-500/5' :
                    entry.result === 'loss' ? 'border-red-500/30 bg-red-500/5' :
                    'border-amber-500/30 bg-amber-500/5'
                  } overflow-hidden transition-all duration-300`}
                  data-testid={`journal-entry-${entry.id}`}
                >
                  {/* Entry Header */}
                  <div 
                    className="p-4 cursor-pointer hover:bg-white/5 transition-colors"
                    onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                          entry.direction === 'buy' ? 'bg-emerald-500/20' : 'bg-red-500/20'
                        }`}>
                          {entry.direction === 'buy' ? (
                            <TrendingUp className="w-5 h-5 text-emerald-400" />
                          ) : (
                            <TrendingDown className="w-5 h-5 text-red-400" />
                          )}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-mono font-bold text-white">{entry.market}</span>
                            <Badge className={entry.direction === 'buy' ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                              {entry.direction.toUpperCase()}
                            </Badge>
                            <Badge className={
                              entry.result === 'win' ? 'bg-emerald-500/20 text-emerald-400' :
                              entry.result === 'loss' ? 'bg-red-500/20 text-red-400' :
                              'bg-amber-500/20 text-amber-400'
                            }>
                              {entry.result?.toUpperCase() || 'OPEN'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-2 text-xs text-slate-500 mt-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(entry.date)}
                            <span className="mx-1">•</span>
                            <span>{entry.trade_type}</span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4">
                        {entry.pnl !== null && (
                          <div className={`text-right ${entry.pnl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            <p className="font-mono font-bold">${entry.pnl >= 0 ? '+' : ''}{entry.pnl?.toFixed(2)}</p>
                            {entry.pips && <p className="text-xs opacity-70">{entry.pips > 0 ? '+' : ''}{entry.pips} pips</p>}
                          </div>
                        )}
                        {isExpanded ? <ChevronUp className="w-5 h-5 text-slate-500" /> : <ChevronDown className="w-5 h-5 text-slate-500" />}
                      </div>
                    </div>
                  </div>
                  
                  {/* Expanded Details */}
                  {isExpanded && (
                    <div className="p-4 border-t border-white/5 bg-black/20">
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4 text-sm">
                        <div>
                          <p className="text-slate-500 text-xs">Entry Price</p>
                          <p className="font-mono text-white">{entry.entry_price}</p>
                        </div>
                        <div>
                          <p className="text-slate-500 text-xs">Exit Price</p>
                          <p className="font-mono text-white">{entry.exit_price || '-'}</p>
                        </div>
                        <div>
                          <p className="text-slate-500 text-xs">Stop Loss</p>
                          <p className="font-mono text-red-400">{entry.stop_loss}</p>
                        </div>
                        <div>
                          <p className="text-slate-500 text-xs">Take Profit</p>
                          <p className="font-mono text-emerald-400">{entry.take_profit}</p>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-4 mb-4 text-sm">
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500">Before:</span>
                          <span className="text-lg">{emotionBefore.emoji}</span>
                          <span className="text-slate-400">{emotionBefore.label}</span>
                        </div>
                        <span className="text-slate-600">→</span>
                        <div className="flex items-center gap-2">
                          <span className="text-slate-500">After:</span>
                          <span className="text-lg">{emotionAfter.emoji}</span>
                          <span className="text-slate-400">{emotionAfter.label}</span>
                        </div>
                      </div>
                      
                      {entry.notes && (
                        <div className="mb-4">
                          <p className="text-xs text-slate-500 mb-1">Notes</p>
                          <p className="text-sm text-slate-300 bg-zinc-800/50 rounded-lg p-3">{entry.notes}</p>
                        </div>
                      )}
                      
                      {entry.lessons && (
                        <div className="mb-4">
                          <p className="text-xs text-slate-500 mb-1">Lessons Learned</p>
                          <p className="text-sm text-amber-400/90 bg-amber-500/10 rounded-lg p-3 border border-amber-500/20">{entry.lessons}</p>
                        </div>
                      )}
                      
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteEntry(entry.id)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-4 h-4 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-zinc-800 bg-zinc-900/50">
        <p className="text-[10px] text-slate-500 text-center">
          Keep a detailed journal to identify patterns and improve your trading psychology
        </p>
      </div>
    </Card>
  );
}
